import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  ArrowBack,
  PersonAdd,
  PersonRemove,
  Settings,
  Search,
  UploadFile,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  getGroupMembers,
  getGroupUsers,
  getOrgPy,
  postGroupIdAddMember,
  postGroupIdRemoveMember,
  updateGroupMemberPermissions,
  GroupMemberPermissions,
  UserResource,
  UserResourceWithGroupMembershipState,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import Papa from "papaparse";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { RubiModal } from "@/common/RubiModal";
import type { CsvRawRow } from "@/components/UserManagement/User/UploadUsersCSVDialog/types";
import { AuthStateContext } from "@/contexts/AuthContext";
// Reuse the exact CSV shape the User Management upload accepts, so a single file
// works for both flows. We only read email/badge_number here to match users.

import "./GroupManagement.scss";

type PermissionRow = {
  id: string | number;
  name: string;
} & GroupMemberPermissions;

const DEFAULT_PERMISSIONS: GroupMemberPermissions = {
  bypass_moderation: false,
  moderate_bulletin_admittance: false,
  manage_members: false,
  manage_admins: false,
  flag_bulletin: false,
  manage_group: false,
  can_share_bulletin: false,
  can_revoke_bulletin: false,
  can_promote_content_moderators: false,
  can_transfer_bulletin_to_another_group: false,
  can_moderate_content: false,
};

const PERMISSION_CONFIGS: {
  key: keyof GroupMemberPermissions;
  label: string;
}[] = [
  { key: "bypass_moderation", label: "Bypass Mod." },
  { key: "moderate_bulletin_admittance", label: "Moderate Adm." },
  { key: "manage_members", label: "Manage Members" },
  { key: "manage_admins", label: "Manage Admins" },
  { key: "manage_group", label: "Manage Group" },
  { key: "can_share_bulletin", label: "Share Bulletin" },
  { key: "can_revoke_bulletin", label: "Revoke Bulletin" },
  { key: "can_promote_content_moderators", label: "Promote Mods" },
  { key: "can_transfer_bulletin_to_another_group", label: "Transfer" },
];

// A user falls into exactly one bucket, checked in priority order: already a
// member, then lacks RubiAlert access, otherwise addable.
type UserStatus = "open" | "blocked" | "member";

// Summary of a CSV import, shown as a dismissible banner after parsing.
type CsvImportReport = {
  fileName: string;
  added: number;
  alreadyMember: number;
  needPermission: number;
  notFound: number;
};

const getUserStatus = (
  user: UserResourceWithGroupMembershipState,
): UserStatus =>
  user.is_group_member ? "member" : !user.is_alert_user ? "blocked" : "open";

const highlightText = (text: string, query: string): JSX.Element => {
  const trimmed = query.trim();
  if (!trimmed) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(trimmed.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + trimmed.length)}</strong>
      {text.slice(idx + trimmed.length)}
    </span>
  );
};

// Fixed width that fits the two action buttons. The data columns use `flex`, so
// DataGrid sizes them to fill the remaining space — the table never overflows
// and this fixed Actions column stays fully visible without horizontal scroll.
const ACTIONS_COLUMN_WIDTH = 160;

const AlertGroupUsersList = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { currentUserGroups, currentUser, checkPermission } =
    useContext(AuthStateContext);

  // DataGrid state
  const [activeMembers, setActiveMembers] = useState<UserResource[]>([]);
  const [searchText, setSearchText] = useState("");
  const [, setLoading] = useState(true);

  // Add user modal state
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [groupUsers, setGroupUsers] = useState<
    UserResourceWithGroupMembershipState[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<UserStatus | "all">("open");
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<
    UserResourceWithGroupMembershipState[]
  >([]);
  const [addPhase, setAddPhase] = useState<"select" | "permissions">("select");
  const [showAllChips, setShowAllChips] = useState(false);
  const [csvReport, setCsvReport] = useState<CsvImportReport | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [permissionRows, setPermissionRows] = useState<PermissionRow[]>([]);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // Remove user modal state
  const [userToRemove, setUserToRemove] = useState<UserResource | null>(null);
  const [isConfirmRemoveUserDialogOpen, setIsConfirmRemoveUserDialogOpen] =
    useState(false);

  const canManageUsers = checkPermission("manage", "users", Number(groupId));

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Match against the debounced search first; the status filter narrows this set.
  // The query is split on commas — a user matches if ANY term hits any field,
  // so "1001, 1002, 1003" surfaces all three badges at once.
  const searchedUsers = useMemo(() => {
    const terms = debouncedSearch
      .toLowerCase()
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (terms.length === 0) return groupUsers;
    return groupUsers.filter((user) => {
      const haystack =
        `${user.first_name} ${user.last_name} ${user.email} ${user.badge_number || ""}`.toLowerCase();
      return terms.some((term) => haystack.includes(term));
    });
  }, [groupUsers, debouncedSearch]);

  const filteredActiveMembers = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!query) return activeMembers;

    return activeMembers.filter((user) => {
      const firstName = (user.first_name || "").toLowerCase();
      const lastName = (user.last_name || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      const badge = (user.badge_number || "").toLowerCase();
      const rank = (user.rank_short || "").toLowerCase();

      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        `${firstName} ${lastName}`.includes(query) ||
        email.includes(query) ||
        badge.includes(query) ||
        rank.includes(query)
      );
    });
  }, [activeMembers, searchText]);

  // Per-status counts for the "Show" dropdown, reflecting the current search.
  const statusCounts = useMemo(() => {
    const counts = { open: 0, blocked: 0, member: 0 };
    searchedUsers.forEach((user) => counts[getUserStatus(user)]++);
    return counts;
  }, [searchedUsers]);

  const filteredUsers = useMemo(() => {
    if (statusFilter === "all") return searchedUsers;
    return searchedUsers.filter((user) => getUserStatus(user) === statusFilter);
  }, [searchedUsers, statusFilter]);

  // Fetch group members on mount
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const result = await getGroupMembers({
          path: { group_id: Number(groupId) },
        });
        const members = result.data ?? [];
        if (members.length > 0) {
          setActiveMembers(members);
        }
      } catch (error) {
        console.error("Error fetching users", error);
        toast.error("Error fetching user data");
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [groupId]);

  const loadGroupUsers = async (orgId: string) => {
    setUsersLoading(true);
    try {
      const result = await getGroupUsers({
        path: { group_id: Number(groupId) },
        query: { org_id: orgId },
      });
      setGroupUsers(result.data ?? []);
    } catch (error) {
      console.error("Error fetching group users", error);
      toast.error("Error fetching users");
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddUserDialogOpen = async () => {
    setIsAddUserDialogOpen(true);
    const defaultOrgId = currentUser?.rubi_org_id || "";
    setSelectedOrgId(defaultOrgId);

    try {
      const result = await getOrgPy();
      setOrgs(result.data?.orgs ?? []);
    } catch (error) {
      console.error("Error fetching organizations", error);
      toast.error("Error loading organizations");
    }

    if (defaultOrgId) {
      await loadGroupUsers(defaultOrgId);
    }
  };

  const handleAddUserDialogClose = () => {
    setIsAddUserDialogOpen(false);
    setSelectedUsers([]);
    setGroupUsers([]);
    setOrgs([]);
    setSearchQuery("");
    setSelectedOrgId("");
    setStatusFilter("open");
    setCsvReport(null);
    setShowAllChips(false);
    setAddPhase("select");
    setPermissionRows([]);
  };

  const handleOrgChange = async (event) => {
    const newOrgId = event.target.value;
    setSelectedOrgId(newOrgId);
    setSelectedUsers([]);
    setSearchQuery("");
    setStatusFilter("open");
    setCsvReport(null);
    setShowAllChips(false);
    setGroupUsers([]);
    if (newOrgId) {
      await loadGroupUsers(newOrgId);
    }
  };

  const handleUserRowClick = (user: UserResourceWithGroupMembershipState) => {
    if (user.is_group_member || !user.is_alert_user) return;
    setSelectedUsers((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user],
    );
  };

  // Parse a CSV (same format as the User Management upload) and auto-select the
  // addable users it references, matching by email first then badge number.
  // Members / users without RubiAlert access / unknown rows are summarized only.
  const handleCsvImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse<CsvRawRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const byEmail = new Map<string, UserResourceWithGroupMembershipState>();
        const byBadge = new Map<string, UserResourceWithGroupMembershipState>();
        groupUsers.forEach((user) => {
          if (user.email) byEmail.set(user.email.toLowerCase().trim(), user);
          if (user.badge_number)
            byBadge.set(user.badge_number.toLowerCase().trim(), user);
        });

        const report: CsvImportReport = {
          fileName: file.name,
          added: 0,
          alreadyMember: 0,
          needPermission: 0,
          notFound: 0,
        };
        const picked = new Set(selectedUsers.map((u) => u.id));
        const toAdd: UserResourceWithGroupMembershipState[] = [];

        results.data.forEach((row) => {
          const email = (row.email ?? "").toLowerCase().trim();
          const badge = (row.badge_number ?? "").toLowerCase().trim();
          const match =
            (email && byEmail.get(email)) ||
            (badge && byBadge.get(badge)) ||
            null;
          if (!match) {
            report.notFound++;
            return;
          }
          const status = getUserStatus(match);
          if (status === "member") {
            report.alreadyMember++;
          } else if (status === "blocked") {
            report.needPermission++;
          } else if (!picked.has(match.id)) {
            picked.add(match.id);
            toAdd.push(match);
            report.added++;
          }
        });

        if (toAdd.length > 0) {
          setSelectedUsers((prev) => [...prev, ...toAdd]);
        }
        setCsvReport(report);
      },
      error: () => toast.error("Could not read CSV file"),
    });

    // Reset so re-selecting the same file fires onChange again.
    event.target.value = "";
  };

  const canManageAdmins = checkPermission("manage", "admins", Number(groupId));

  const handleAddUserToGroup = async () => {
    if (selectedUsers.length === 0) return;
    setAddLoading(true);
    try {
      await Promise.all(
        selectedUsers.map((user) =>
          postGroupIdAddMember({
            path: { group_id: Number(groupId) },
            body: { id: user.id },
          }),
        ),
      );
      toast.success(
        selectedUsers.length === 1
          ? "User added to group"
          : `${selectedUsers.length} users added to group`,
      );
      const updated = await getGroupMembers({
        path: { group_id: Number(groupId) },
      });
      setActiveMembers(updated.data ?? []);

      if (canManageAdmins) {
        setPermissionRows(
          selectedUsers.map((user) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`,
            ...DEFAULT_PERMISSIONS,
          })),
        );
        setAddPhase("permissions");
      } else {
        handleAddUserDialogClose();
      }
    } catch (error) {
      console.error("Error adding user", error);
      toast.error("Error adding user to group");
    } finally {
      setAddLoading(false);
    }
  };

  const togglePermissionForUser = (
    userId: string | number,
    key: keyof GroupMemberPermissions,
  ) => {
    setPermissionRows((prev) =>
      prev.map((row) =>
        row.id === userId ? { ...row, [key]: !row[key] } : row,
      ),
    );
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await Promise.all(
        permissionRows.map(({ id, ...perms }) =>
          updateGroupMemberPermissions({
            path: { group_id: Number(groupId), user_id: String(id) },
            body: perms,
          }),
        ),
      );
      toast.success("Permissions saved");
    } catch (error) {
      console.error("Error saving permissions", error);
      toast.error("Error saving permissions");
    } finally {
      setSavingPermissions(false);
      handleAddUserDialogClose();
    }
  };

  const toggleAllPermission = (key: keyof GroupMemberPermissions) => {
    const allChecked = permissionRows.every((r) => r[key]);
    setPermissionRows((prev) =>
      prev.map((row) => ({ ...row, [key]: !allChecked })),
    );
  };

  const permissionColumns: GridColDef[] = [
    { field: "name", headerName: "User", width: 180, sortable: false },
    ...PERMISSION_CONFIGS.map(({ key, label }) => {
      const checkedCount = permissionRows.filter((r) => r[key]).length;
      const allChecked =
        permissionRows.length > 0 && checkedCount === permissionRows.length;
      const indeterminate =
        checkedCount > 0 && checkedCount < permissionRows.length;

      return {
        field: key,
        headerName: label,
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                whiteSpace: "normal",
                textAlign: "center",
                lineHeight: 1.2,
              }}
            >
              {label}
            </Typography>
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={indeterminate}
              disabled={permissionRows.length === 0}
              onChange={() => toggleAllPermission(key)}
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0 }}
            />
          </Box>
        ),
        renderCell: (params) => (
          <Checkbox
            size="small"
            checked={!!params.row[key]}
            onChange={() => togglePermissionForUser(params.row.id, key)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      };
    }),
  ];

  const handleRemoveUserFromGroup = async (userId) => {
    try {
      await postGroupIdRemoveMember({
        path: { group_id: Number(groupId) },
        body: { id: userId },
      });
      toast.success("User removed from group");
      const updated = await getGroupMembers({
        path: { group_id: Number(groupId) },
      });
      setActiveMembers(updated.data ?? []);
    } catch (error) {
      console.error("Error removing user", error);
      toast.error("Error removing user from group");
    }
  };

  const columns: GridColDef[] = [
    { field: "first_name", headerName: "First Name", flex: 2, minWidth: 120 },
    { field: "last_name", headerName: "Last Name", flex: 2, minWidth: 120 },
    { field: "email", headerName: "Email", flex: 3, minWidth: 180 },
    { field: "badge_number", headerName: "Badge", flex: 1, minWidth: 80 },
    { field: "rank_short", headerName: "Rank", flex: 1, minWidth: 80 },
  ];

  if (canManageUsers) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: ACTIONS_COLUMN_WIDTH,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          {checkPermission("manage", "admins", Number(groupId)) && (
            <Button
              color="primary"
              startIcon={<Settings />}
              onClick={() =>
                navigate(
                  `/rubialert/manage-groups/${groupId}/members/${params.row.id}/permissions`,
                )
              }
              title="Manage Permissions"
            />
          )}
          {checkPermission("manage", "users", Number(groupId)) && (
            <Button
              color="primary"
              startIcon={<PersonRemove />}
              onClick={() => {
                setUserToRemove(params.row);
                setIsConfirmRemoveUserDialogOpen(true);
              }}
              title="Remove User"
            />
          )}
        </Box>
      ),
    });
  }

  return (
    <div className="users_list_container">
      <div id="top-page-div"></div>

      <Grid container alignItems="center" justifyContent="center">
        <Grid size={{ xs: 4 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={() =>
              navigate(`/rubialert/manage-groups/${groupId}`, { replace: true })
            }
          >
            Back
          </Button>
        </Grid>

        <Grid size={{ xs: 4 }} style={{ textAlign: "center" }}>
          <Typography variant="h4" gutterBottom>
            {
              currentUserGroups.find((group) => group.id === Number(groupId))
                ?.name
            }{" "}
            Group Members
          </Typography>
        </Grid>

        <Grid
          size={{ xs: 4 }}
          style={{
            textAlign: "right",
          }}
        >
          {checkPermission("manage", "users", Number(groupId)) && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAdd />}
              onClick={handleAddUserDialogOpen}
            >
              Add User
            </Button>
          )}
        </Grid>
      </Grid>
      <Grid container justifyContent="flex-end" sx={{ width: "100%", mb: 2 }}>
        <Grid
          sx={{
            width: {
              xs: "100%",
              md: "30%",
            },
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t("groupMembers.searchPlaceholder")}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            inputProps={{
              "aria-label": t("groupMembers.searchAriaLabel"),
            }}
          />
        </Grid>
      </Grid>
      <DataGrid
        rows={filteredActiveMembers}
        columns={columns}
        rowSelection={false}
        sx={{
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            { whiteSpace: "normal", wordWrap: "break-word" },
        }}
      />

      {/* Add User Dialog */}
      <Dialog
        open={isAddUserDialogOpen}
        onClose={handleAddUserDialogClose}
        maxWidth={addPhase === "permissions" ? "lg" : "sm"}
        fullWidth
      >
        <DialogTitle>
          {addPhase === "permissions"
            ? "Set Permissions for New Members"
            : "Add User to Group"}
        </DialogTitle>
        <DialogContent>
          {addPhase === "permissions" ? (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Optionally set group permissions for the newly added members.
              </Typography>
              <DataGrid
                rows={permissionRows}
                columns={permissionColumns}
                rowSelection={false}
                hideFooter
                columnHeaderHeight={72}
                sx={{
                  "& .MuiDataGrid-columnHeader": {
                    overflow: "visible",
                    alignItems: "flex-start",
                    pt: 0.5,
                  },
                  "& .MuiDataGrid-columnHeaderDraggableContainer": {
                    height: "100%",
                  },
                }}
              />
            </Box>
          ) : (
            <Box mt={1}>
              {/* Selected users — removable chips, collapsed past 10 */}
              {selectedUsers.length > 0 && (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}
                >
                  {(showAllChips
                    ? selectedUsers
                    : selectedUsers.slice(0, 10)
                  ).map((user) => (
                    <Chip
                      key={user.id}
                      size="small"
                      label={`${user.first_name} ${user.last_name}`}
                      onDelete={() => handleUserRowClick(user)}
                    />
                  ))}
                  {selectedUsers.length > 10 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        showAllChips
                          ? "Show fewer"
                          : `+${selectedUsers.length - 10} more`
                      }
                      onClick={() => setShowAllChips((v) => !v)}
                    />
                  )}
                </Box>
              )}

              {/* Agency + status filter dropdowns */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Agency</InputLabel>
                  <Select
                    value={selectedOrgId}
                    label="Agency"
                    variant="outlined"
                    onChange={handleOrgChange}
                    disabled={addLoading}
                  >
                    {orgs.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Show</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Show"
                    variant="outlined"
                    onChange={(e) =>
                      setStatusFilter(e.target.value as UserStatus | "all")
                    }
                    disabled={addLoading || !selectedOrgId}
                  >
                    <MenuItem value="open">
                      Can be added ({statusCounts.open})
                    </MenuItem>
                    <MenuItem value="blocked">
                      Need RubiAlert permission ({statusCounts.blocked})
                    </MenuItem>
                    <MenuItem value="member">
                      Already in group ({statusCounts.member})
                    </MenuItem>
                    <MenuItem value="all">
                      Everyone ({searchedUsers.length})
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Search input */}
              <TextField
                fullWidth
                label="Search by name, email, or badge"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={addLoading || !selectedOrgId}
                sx={{ mb: 1 }}
              />

              {/* Import from CSV */}
              <Box sx={{ mb: 1 }}>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  aria-label="Import users from CSV"
                  onChange={handleCsvImport}
                  style={{ display: "none" }}
                />
                <Button
                  size="small"
                  startIcon={<UploadFile />}
                  onClick={() => csvInputRef.current?.click()}
                  disabled={addLoading || usersLoading || !selectedOrgId}
                >
                  Import from CSV
                </Button>
              </Box>

              {/* CSV import summary */}
              {csvReport && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    p: 1.5,
                    mb: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    <strong>{csvReport.fileName}</strong>: {csvReport.added}{" "}
                    selected
                    {csvReport.alreadyMember > 0 &&
                      ` · ${csvReport.alreadyMember} already in group`}
                    {csvReport.needPermission > 0 &&
                      ` · ${csvReport.needPermission} need RubiAlert permission`}
                    {csvReport.notFound > 0 &&
                      ` · ${csvReport.notFound} not found`}
                  </Typography>
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => setCsvReport(null)}
                    sx={{ ml: "auto", minWidth: 0, flex: "none" }}
                  >
                    Dismiss
                  </Button>
                </Box>
              )}

              {/* Users list */}
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                {usersLoading ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredUsers.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ p: 2, textAlign: "center" }}
                  >
                    {!selectedOrgId
                      ? "Select an agency to view users"
                      : searchedUsers.length === 0
                        ? searchQuery
                          ? "No users match your search"
                          : "No users in this organization"
                        : "No users match this filter"}
                  </Typography>
                ) : (
                  filteredUsers.map((user) => {
                    const isDisabled =
                      user.is_group_member || !user.is_alert_user;
                    const isSelected = selectedUsers.some(
                      (u) => u.id === user.id,
                    );
                    const displayName = `${user.first_name} ${user.last_name} (${user.rank_short} ${user.badge_number})`;
                    return (
                      <Box
                        key={user.id}
                        onClick={() => handleUserRowClick(user)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 2,
                          py: 1,
                          cursor: isDisabled ? "default" : "pointer",
                          opacity: user.is_group_member ? 0.5 : 1,
                          backgroundColor: isSelected
                            ? "action.selected"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: !isDisabled
                              ? isSelected
                                ? "action.selected"
                                : "action.hover"
                              : "transparent",
                          },
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => handleUserRowClick(user)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0 }}
                          />
                          <Box>
                            <Typography variant="body2">
                              {highlightText(displayName, debouncedSearch)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {highlightText(user.email, debouncedSearch)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          {user.is_group_member && (
                            <Typography variant="caption" color="textSecondary">
                              Already in group
                            </Typography>
                          )}
                          {!user.is_alert_user && !user.is_group_member && (
                            <Tooltip title="Coming soon">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled
                                >
                                  Invite User
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleAddUserDialogClose}
            color="secondary"
            disabled={addLoading || savingPermissions}
          >
            {addPhase === "permissions" ? "Skip" : "Cancel"}
          </Button>
          {addPhase === "select" ? (
            <Button
              onClick={handleAddUserToGroup}
              color="primary"
              variant="contained"
              disabled={selectedUsers.length === 0 || addLoading}
            >
              {addLoading ? <CircularProgress size={24} /> : "Add"}
            </Button>
          ) : (
            <Button
              onClick={handleSavePermissions}
              color="primary"
              variant="contained"
              disabled={savingPermissions}
            >
              {savingPermissions ? (
                <CircularProgress size={24} />
              ) : (
                "Save Permissions"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm Remove User */}
      <RubiModal
        isModalOpenArg={isConfirmRemoveUserDialogOpen}
        setIsModalOpenArg={setIsConfirmRemoveUserDialogOpen}
        onClose={() => {}}
      >
        <DialogTitle>Remove User from Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {userToRemove?.first_name}{" "}
            {userToRemove?.last_name} from the group?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsConfirmRemoveUserDialogOpen(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleRemoveUserFromGroup(userToRemove ? userToRemove.id : "");
              setIsConfirmRemoveUserDialogOpen(false);
            }}
            color="primary"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </RubiModal>
    </div>
  );
};

export default memo(AlertGroupUsersList);
